// ADR 30.9.26l §2/§4.1 — the question a stranger is asked once.
//
// Equal weight, not modal: Decline and Accept are one `Button` variant and
// size, side by side, Decline first in DOM order. An `aside` with no focus
// trap and no backdrop that never takes focus — a learner can ignore it and
// study, and ignoring it means denied.
//
// The visual shape is `InstallStrip`'s, whose slot this shares (§4.1).

import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuthSession } from '../auth/session'
import { Anchor } from '../ui/Anchor'
import { Button } from '../ui/Button'
import { showSnackbar } from '../ui/feedback'
import { acceptConsent, declineConsent } from './answerConsent'

/** `LegalStrip`'s policy URL; not imported, so the shell chunk carries no ads code. */
const PRIVACY_URL = 'https://getlingara.com/privacy/'

function useAnswers(compact: boolean) {
  const { t } = useTranslation()
  const { userId } = useAuthSession()
  const [busy, setBusy] = useState(false)
  const accept = async () => {
    setBusy(true)
    const outcome = await acceptConsent(userId, compact)
    setBusy(false)
    if (outcome === 'failed') showSnackbar({ message: t('error_unknown') })
    else if (outcome === 'next_load') showSnackbar({ message: t('settings_privacy_next_visit') })
  }
  return { busy, accept: () => void accept(), decline: declineConsent }
}

/** `compact` is the one-line `/s/:token` variant: no title, body, link, buttons. */
export function ConsentStrip({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation()
  const titleId = useId()
  const { busy, accept, decline } = useAnswers(compact)
  return (
    <aside
      aria-labelledby={compact ? undefined : titleId}
      aria-label={compact ? t('consent_banner_title') : undefined}
      data-testid="consent-strip"
      data-compact={compact}
      className="flex flex-wrap items-center gap-3 border-b border-boundary bg-surface-sunken px-4 py-2"
    >
      <div className="min-w-0 flex-1 text-body-md">
        {compact ? null : (
          <p id={titleId} className="font-medium">
            {t('consent_banner_title')}
          </p>
        )}
        <p className="text-ink-muted">
          {t('consent_banner_body')}{' '}
          <Anchor variant="quiet" href={PRIVACY_URL}>
            {t('legal_strip_privacy')}
          </Anchor>
        </p>
      </div>
      <Button variant="secondary" disabled={busy} onClick={decline}>
        {t('consent_banner_decline')}
      </Button>
      <Button variant="secondary" disabled={busy} onClick={accept}>
        {t('consent_banner_accept')}
      </Button>
    </aside>
  )
}
